import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PlusIcon, ArrowLeftIcon, BanknotesIcon, ArrowUpTrayIcon, CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import { getERAs, createERA, getClaims, parseERA835, postERAFrom835 } from '../services/insuranceService';
import EmptyState from '../components/common/EmptyState';
import Modal from '../components/common/Modal';
import Badge from '../components/common/Badge';
import TableSkeleton from '../components/common/skeletons/TableSkeleton';

const fmt = (val) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val ?? 0);

const eraSchema = z.object({
  payerName: z.string().min(1, 'Payer name is required'),
  payerId: z.string().optional().or(z.literal('')),
  checkNumber: z.string().optional().or(z.literal('')),
  paymentDate: z.string().min(1, 'Payment date is required'),
  totalPaid: z.coerce.number().min(0, 'Must be non-negative'),
  notes: z.string().optional().or(z.literal('')),
});

function ERAForm({ pendingClaims, onSubmit, onCancel, isSubmitting }) {
  const [selectedClaimIds, setSelectedClaimIds] = useState([]);
  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(eraSchema),
    defaultValues: { paymentDate: format(new Date(), 'yyyy-MM-dd') },
  });

  const toggleClaim = (id) => {
    setSelectedClaimIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const submit = (data) => onSubmit({ ...data, claimIds: selectedClaimIds });

  return (
    <form onSubmit={handleSubmit(submit)} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Payer name *</label>
          <input type="text" className="input" {...register('payerName')} />
          {errors.payerName && <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.payerName.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Payer ID</label>
          <input type="text" className="input" {...register('payerId')} />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Check #</label>
          <input type="text" className="input" {...register('checkNumber')} />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Payment date *</label>
          <input type="date" className="input" {...register('paymentDate')} />
          {errors.paymentDate && <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.paymentDate.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Total paid *</label>
          <input type="number" step="0.01" min="0" className="input" {...register('totalPaid')} />
          {errors.totalPaid && <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.totalPaid.message}</p>}
        </div>
      </div>

      {pendingClaims.length > 0 && (
        <div>
          <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            Attach claims ({selectedClaimIds.length} selected)
          </p>
          <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg max-h-48 overflow-y-auto divide-y divide-neutral-100 dark:divide-neutral-700">
            {pendingClaims.map((claim) => (
              <label key={claim.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedClaimIds.includes(claim.id)}
                  onChange={() => toggleClaim(claim.id)}
                  className="rounded"
                />
                <span className="text-sm text-neutral-700 dark:text-neutral-300 flex-1">
                  {claim.client ? `${claim.client.firstName} ${claim.client.lastName}` : '—'}
                  {' · '}{claim.payerName}
                  {' · '}{fmt(claim.billedAmount)}
                </span>
                <span className="text-xs text-neutral-400 dark:text-neutral-500">{claim.status}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Notes</label>
        <textarea className="input" rows={2} {...register('notes')} />
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
        <button type="submit" disabled={isSubmitting} className="btn btn-primary disabled:opacity-60">
          {isSubmitting ? 'Posting…' : 'Post ERA'}
        </button>
      </div>
    </form>
  );
}

const STATUS_ICONS = {
  paid: <CheckCircleIcon className="h-4 w-4 text-green-500" />,
  partial: <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />,
  denied: <XCircleIcon className="h-4 w-4 text-red-500" />,
};

function ERA835Preview({ preview, onConfirm, onCancel, isPosting }) {
  const { payerName, payerId, checkNumber, paymentDate, totalPaid, claims, matchedCount, unmatchedCount } = preview;

  return (
    <div className="space-y-5">
      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 bg-neutral-50 dark:bg-neutral-800/60 rounded-lg text-sm">
        <div>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-0.5">Payer</p>
          <p className="font-medium text-neutral-900 dark:text-neutral-100">{payerName || '—'}</p>
        </div>
        <div>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-0.5">Check #</p>
          <p className="font-medium text-neutral-900 dark:text-neutral-100">{checkNumber || '—'}</p>
        </div>
        <div>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-0.5">Payment date</p>
          <p className="font-medium text-neutral-900 dark:text-neutral-100">
            {paymentDate ? format(parseISO(paymentDate), 'MMM d, yyyy') : '—'}
          </p>
        </div>
        <div>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-0.5">Total paid</p>
          <p className="font-medium text-green-600 dark:text-green-400">{fmt(totalPaid)}</p>
        </div>
      </div>

      {/* Match summary */}
      <div className="flex gap-4 text-sm">
        <span className="text-green-700 dark:text-green-400 font-medium">{matchedCount} matched</span>
        {unmatchedCount > 0 && (
          <span className="text-yellow-700 dark:text-yellow-400 font-medium">{unmatchedCount} unmatched (will be skipped)</span>
        )}
      </div>

      {/* Claims table */}
      <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden max-h-72 overflow-y-auto">
        <table className="w-full text-xs min-w-[500px]">
          <thead className="bg-neutral-50 dark:bg-neutral-900/50 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2 text-neutral-500 dark:text-neutral-400 font-semibold uppercase tracking-wide">Claim #</th>
              <th className="text-left px-3 py-2 text-neutral-500 dark:text-neutral-400 font-semibold uppercase tracking-wide">Client</th>
              <th className="text-right px-3 py-2 text-neutral-500 dark:text-neutral-400 font-semibold uppercase tracking-wide">Billed</th>
              <th className="text-right px-3 py-2 text-neutral-500 dark:text-neutral-400 font-semibold uppercase tracking-wide">Paid</th>
              <th className="text-center px-3 py-2 text-neutral-500 dark:text-neutral-400 font-semibold uppercase tracking-wide">Result</th>
              <th className="text-center px-3 py-2 text-neutral-500 dark:text-neutral-400 font-semibold uppercase tracking-wide">Match</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
            {claims.map((c, i) => (
              <tr key={i} className={`${!c.matchedClaimId ? 'opacity-50' : ''} hover:bg-neutral-50 dark:hover:bg-neutral-700/30`}>
                <td className="px-3 py-2 font-mono text-neutral-700 dark:text-neutral-300">{c.claimNumber}</td>
                <td className="px-3 py-2 text-neutral-700 dark:text-neutral-300">
                  {c.matchedClient
                    ? `${c.matchedClient.firstName} ${c.matchedClient.lastName}`
                    : <span className="text-neutral-400 italic">not found</span>}
                </td>
                <td className="px-3 py-2 text-right text-neutral-700 dark:text-neutral-300">{fmt(c.billedAmount)}</td>
                <td className="px-3 py-2 text-right font-medium text-green-600 dark:text-green-400">{fmt(c.paidAmount)}</td>
                <td className="px-3 py-2 text-center">
                  <span className="flex items-center justify-center gap-1">
                    {STATUS_ICONS[c.resolvedStatus]}
                    {c.discrepancy && <ExclamationTriangleIcon className="h-3.5 w-3.5 text-yellow-500" title="Billed amount discrepancy" />}
                  </span>
                </td>
                <td className="px-3 py-2 text-center">
                  {c.matchedClaimId
                    ? <Badge label="matched" variant="success" />
                    : <Badge label="unmatched" variant="default" />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end gap-3 pt-1">
        <button type="button" onClick={onCancel} className="btn btn-secondary">Back</button>
        <button
          type="button"
          onClick={() => onConfirm({ payerName, payerId, checkNumber, paymentDate, totalPaid, claims })}
          disabled={isPosting || matchedCount === 0}
          className="btn btn-primary disabled:opacity-60"
        >
          {isPosting ? 'Posting…' : `Post ERA (${matchedCount} claim${matchedCount !== 1 ? 's' : ''})`}
        </button>
      </div>
    </div>
  );
}

export default function ERAList() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [is835Open, setIs835Open] = useState(false);
  const [preview835, setPreview835] = useState(null);
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['eras'],
    queryFn: getERAs,
  });

  const { data: pendingData } = useQuery({
    queryKey: ['claims', { status: 'pending' }],
    queryFn: () => getClaims({ status: 'pending', limit: 200 }),
    enabled: isFormOpen,
  });
  const { data: submittedData } = useQuery({
    queryKey: ['claims', { status: 'submitted' }],
    queryFn: () => getClaims({ status: 'submitted', limit: 200 }),
    enabled: isFormOpen,
  });

  const pendingClaims = [
    ...(pendingData?.claims ?? []),
    ...(submittedData?.claims ?? []),
  ];

  const createMutation = useMutation({
    mutationFn: createERA,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eras'] });
      queryClient.invalidateQueries({ queryKey: ['claims'] });
      setIsFormOpen(false);
      toast.success('ERA posted');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to post ERA'),
  });

  const parseMutation = useMutation({
    mutationFn: parseERA835,
    onSuccess: (data) => setPreview835(data),
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to parse 835 file'),
  });

  const postMutation = useMutation({
    mutationFn: postERAFrom835,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eras'] });
      queryClient.invalidateQueries({ queryKey: ['claims'] });
      setIs835Open(false);
      setPreview835(null);
      toast.success('ERA posted from 835');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to post ERA'),
  });

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    parseMutation.mutate(file);
    e.target.value = '';
  };

  const handleConfirm = (parsed) => {
    postMutation.mutate({
      payerName: parsed.payerName,
      payerId: parsed.payerId,
      checkNumber: parsed.checkNumber,
      paymentDate: parsed.paymentDate,
      totalPaid: parsed.totalPaid,
      claims: parsed.claims
        .filter((c) => c.matchedClaimId)
        .map((c) => ({
          matchedClaimId: c.matchedClaimId,
          paidAmount: c.paidAmount,
          resolvedStatus: c.resolvedStatus,
          adjustments: c.adjustments,
        })),
    });
  };

  const eras = data?.eras ?? [];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link to="/insurance" className="text-neutral-400 dark:text-neutral-500 hover:text-neutral-600">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">ERA Log</h1>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".835,.edi,.txt,.x12"
            className="hidden"
            onChange={handleFileSelect}
          />
          <button
            onClick={() => { setPreview835(null); setIs835Open(true); }}
            className="btn btn-secondary flex items-center gap-2"
          >
            <ArrowUpTrayIcon className="h-4 w-4" />
            Upload 835
          </button>
          <button onClick={() => setIsFormOpen(true)} className="btn btn-primary flex items-center gap-2">
            <PlusIcon className="h-4 w-4" />
            Post ERA
          </button>
        </div>
      </div>

      {isLoading ? (
        <TableSkeleton rows={5} />
      ) : isError ? (
        <div className="text-center py-20 text-danger-600 dark:text-danger-400">Failed to load ERAs.</div>
      ) : eras.length === 0 ? (
        <EmptyState
          icon={BanknotesIcon}
          title="No ERAs posted yet"
          description="Post an ERA to record a bulk insurance payment across multiple claims."
          action={<button onClick={() => setIsFormOpen(true)} className="btn btn-primary">Post ERA</button>}
        />
      ) : (
        <div className="card p-0 overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-neutral-50 dark:bg-neutral-900/50 border-b border-neutral-200 dark:border-neutral-700">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Payer</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Check #</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Payment date</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Total paid</th>
                <th className="text-center px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide"># Claims</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
              {eras.map((era) => (
                <tr key={era.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors">
                  <td className="px-6 py-4 font-medium text-neutral-900 dark:text-neutral-100">{era.payerName}</td>
                  <td className="px-6 py-4 text-neutral-500 dark:text-neutral-400">{era.checkNumber || '—'}</td>
                  <td className="px-6 py-4 text-neutral-600 dark:text-neutral-400">{format(parseISO(era.paymentDate), 'MMM d, yyyy')}</td>
                  <td className="px-6 py-4 text-right font-medium text-green-600 dark:text-green-400">{fmt(era.totalPaid)}</td>
                  <td className="px-6 py-4 text-center text-neutral-600 dark:text-neutral-400">{era.claims?.length ?? 0}</td>
                  <td className="px-6 py-4 text-right">
                    <Link to={`/insurance/eras/${era.id}`} className="text-primary-600 hover:underline text-sm">
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title="Post ERA" size="lg">
        <ERAForm
          pendingClaims={pendingClaims}
          onSubmit={(data) => createMutation.mutate(data)}
          onCancel={() => setIsFormOpen(false)}
          isSubmitting={createMutation.isPending}
        />
      </Modal>

      <Modal
        isOpen={is835Open}
        onClose={() => { setIs835Open(false); setPreview835(null); }}
        title={preview835 ? 'Review 835 ERA' : 'Upload X12 835 File'}
        size="xl"
      >
        {preview835 ? (
          <ERA835Preview
            preview={preview835}
            onConfirm={handleConfirm}
            onCancel={() => setPreview835(null)}
            isPosting={postMutation.isPending}
          />
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Upload an X12 835 Health Care Claim Payment/Advice file. Sage will parse it, match claims by control number, and show you a preview before posting.
            </p>
            <div
              className="border-2 border-dashed border-neutral-300 dark:border-neutral-600 rounded-xl p-10 text-center cursor-pointer hover:border-primary-400 transition-colors"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer.files?.[0];
                if (file) parseMutation.mutate(file);
              }}
            >
              <ArrowUpTrayIcon className="h-10 w-10 mx-auto text-neutral-400 dark:text-neutral-500 mb-3" />
              {parseMutation.isPending ? (
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Parsing…</p>
              ) : (
                <>
                  <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    Drop your .835 file here, or <span className="text-primary-600 hover:underline">browse</span>
                  </p>
                  <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">.835, .edi, .txt, .x12 — max 5 MB</p>
                </>
              )}
            </div>
            <div className="flex justify-end">
              <button type="button" onClick={() => setIs835Open(false)} className="btn btn-secondary">Cancel</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
